import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import type { Maybe } from '@/types';
import { delay } from 'betterplace-utils';
export const AudioState = {
  Initial: 'initial',
  Ready: 'ready',
  Playing: 'playing',
  PermissionRequired: 'permission'
} as const;
type AudioState = (typeof AudioState)[keyof typeof AudioState];
function getAudioState(ready: boolean, playbackEnabled: boolean, playbackInProgress: boolean): AudioState {
  if (!ready) return AudioState.Initial;
  if (!playbackEnabled) return AudioState.PermissionRequired;
  if (playbackInProgress) return AudioState.Playing;
  return AudioState.Ready;
}
type NotAllowedError = {
  name: 'NotAllowedError';
};
function isNotAllowedError(error: unknown): error is NotAllowedError {
  return typeof error === 'object' && !!error && 'name' in error && error.name === 'NotAllowedError';
}
function useAudio({
  source,
  volume,
  playWithSound
}: {
  source: Maybe<string>;
  volume: number;
  playWithSound: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const noSound = !playWithSound || !volume;
  const [ready, setReady] = useState(false);
  const isReadyRef = useRef(false);
  const [playbackEnabled_, setPlaybackEnabled] = useState(true);
  const playbackEnabled = !noSound && playbackEnabled_;
  const [playbackInProgress, setPlaybackInProgress] = useState(false);
  const state = useMemo(() => getAudioState(ready, playbackEnabled, playbackInProgress), [ready, playbackEnabled, playbackInProgress]);
  const onPlaybackFinished = useCallback(() => {
    setPlaybackInProgress(false);
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.pause();
  }, []);
  const play = useCallback(
  /**
   *
   * @param muted - whether the audio should be muted disregarding the volume setting
   * @param playbackStarted - callback to be called when the audio starts playing (e.g. to start a timer)
   */
  (muted?: boolean, playbackStarted?: () => void) => {
    async function playAudio() {
      if (noSound) return;
      while (!isReadyRef.current) {
        await delay(33);
        if (!audioRef.current) return;
        if (!audioRef.current.src) return;
      }
      const audio = audioRef.current!;
      audio.volume = volume;
      const origVolume = audio.volume;
      if (muted) {
        audio.volume = 0.01;
      }
      try {
        playbackStarted?.();
        await audio.play.bind(audio)();
        setPlaybackEnabled(true);
        setPlaybackInProgress(true);
        audio.addEventListener('ended', onPlaybackFinished, {
          once: true
        });
      } catch (error) {
        console.error(error);
        if (!isNotAllowedError(error)) return;
        setPlaybackEnabled(false);
      } finally {
        if (audioRef.current) audioRef.current.volume = origVolume;
      }
    }
    void playAudio();
  }, [volume, noSound, onPlaybackFinished]);
  const pause = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setPlaybackInProgress(false);
  }, []);
  const onDataLoaded = useEffectEvent(() => {
    setReady(true);
    isReadyRef.current = true;
  });
  useEffect(() => {
    if (!source || !playWithSound || volume === 0) return;
    let audio_0 = audioRef.current;
    if (!audio_0) {
      audio_0 = new Audio(source);
      audioRef.current = audio_0;
    }
    setReady(false);
    isReadyRef.current = false;
    audio_0.src = source;
    audio_0.loop = false;
    audio_0.volume = volume;
    audio_0.autoplay = false;
    audio_0.load();
    audio_0.addEventListener('canplaythrough', onDataLoaded);
    return () => {
      audio_0.currentTime = 0;
      audio_0.pause();
      audio_0.removeEventListener('canplaythrough', onDataLoaded);
      audio_0.removeEventListener('ended', onPlaybackFinished);
      setReady(false);
      isReadyRef.current = false;
      setPlaybackEnabled(true);
      setPlaybackInProgress(false);
    };
  }, [source, playWithSound, volume, onPlaybackFinished]);
  return {
    play,
    pause,
    stop: onPlaybackFinished,
    state,
    _ref: audioRef
  };
}
export default useAudio;